/* eslint-disable import/exports-last */
// eslint-disable-next-line consistent-default-export-name/default-export-match-filename
import classNames from 'classnames'
import styles from './Confetti.module.css'
import { Particle, getConfettiKeyframeStyles, getConfettoStyle, rotationKeyframeStyles } from './styles'
import { createPortal } from 'react-dom'
import { range } from '@betterplace/utils'
import { rotationTransforms, shouldBeCircle } from './utils'
import { useEffect, useMemo, useRef, useState } from 'react'

const FORCE = 0.5 // 0-1 roughly the vertical force at which particles initially explode
const SIZE = 12 // max height for particle rectangles, diameter for particle circles
const HEIGHT = '120vh' // distance particles will fall from initial explosion point
const WIDTH = 1000 // horizontal spread of particles in pixels
const PARTICLE_COUNT = 100
const DURATION = 2200
const COLORS = ['#FFC700', '#FF0000', '#2E3191', '#41BBC7']

export type ConfettiProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'ref'> & {
  particleCount?: number
  duration?: number
  colors?: string[]
  particleSize?: number
  force?: number
  height?: number | string
  width?: number
  zIndex?: number
  onComplete?: () => void
}

function createParticles(count: number, colors: string[]): Particle[] {
  const increment = 360 / count
  return Array.from(range(count)).map((index) => ({
    color: colors[index % colors.length]!,
    degree: increment * index,
  }))
}

function Confetti({
  particleCount = PARTICLE_COUNT,
  duration = DURATION,
  colors = COLORS,
  particleSize = SIZE,
  force = FORCE,
  height = HEIGHT,
  width = WIDTH,
  zIndex,
  onComplete,
  ...props
}: ConfettiProps) {
  const [origin, setOrigin] = useState<{ top: number; left: number }>()
  const particles = useMemo(() => createParticles(particleCount, colors), [particleCount, colors])
  const degrees = useMemo(() => particles.map((particle) => particle.degree), [particles])
  const keyframeStyles = useMemo(() => getConfettiKeyframeStyles(degrees, height, width), [degrees, width, height])
  const originRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = originRef.current
    if (!node) return
    let offsetTop = 0
    let offsetLeft = 0
    const container = node.closest('dialog')?.firstElementChild
    if (container) {
      const rec = container.getBoundingClientRect()
      offsetTop = rec.top
      offsetLeft = rec.left
    }
    const { top, left } = node.getBoundingClientRect()
    setOrigin({ top: top - offsetTop, left: left - offsetLeft })
  }, [])

  useEffect(() => {
    if (typeof onComplete !== 'function') return
    const timeout = setTimeout(onComplete, duration)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration])

  return (
    <div ref={originRef} className={styles.container} {...props}>
      {origin &&
        createPortal(
          <div
            className={styles.screen}
            style={{ ...rotationKeyframeStyles, ...keyframeStyles, ...(zIndex ? { zIndex } : null) }}
          >
            <div style={{ position: 'absolute', top: origin.top, left: origin.left }}>
              {particles.map((particle, i) => {
                const rotationIndex = Math.round(Math.random() * (rotationTransforms.length - 1))
                const isCircle = shouldBeCircle(rotationIndex)
                const particleStyles = getConfettoStyle(particle, duration, force, particleSize, isCircle)
                return (
                  <div
                    className={classNames(styles.particle, styles[`rotation-${rotationIndex}`], {
                      [styles.circle!]: isCircle,
                    })}
                    key={particle.degree}
                    style={particleStyles}
                  >
                    <div></div>
                  </div>
                )
              })}
            </div>
          </div>,
          // we target the inner modal div
          document.getElementById('bp-portal-target-inert') ?? document.body
        )}
    </div>
  )
}

export default Confetti
